import type { FC } from 'react';
import { Button } from 'antd';
import { WealthScreenSkeleton } from '@wavvest/wealth-ui';
import '@wavvest/wealth-ui/wealth-screen-skeleton.scss';
import './route-loading-skeleton.scss';

type RouteSkeletonVariant =
  | 'dashboard'
  | 'clients'
  | 'wealth-detail'
  | 'client-profile'
  | 'client-detail'
  | 'meeting-hub'
  | 'planner'
  | 'subscription'
  | 'filehub'
  | 'members'
  | 'settings'
  | 'transaction-history'
  | 'table'
  | 'generic';

type AdvisorRouteErrorProps = {
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
};

const repeat = (count: number): number[] => Array.from({ length: count }, (_, index) => index);

const isMeetingHubSkeletonRoute = (pathname: string): boolean =>
  /^\/home\/meetinghub(?:\/|$)/.test(pathname);

export const getRouteSkeletonVariant = (pathname: string): RouteSkeletonVariant => {
  if (pathname.startsWith('/home/dashboard')) {
    return 'dashboard';
  }

  if (pathname === '/home/clients/new') {
    return 'client-detail';
  }

  if (/^\/home\/clients\/[^/]+\/?$/.test(pathname)) {
    return 'wealth-detail';
  }

  if (/^\/home\/clients\/[^/]+\/profile\/?$/.test(pathname)) {
    return 'client-profile';
  }

  if (pathname.startsWith('/home/clients/')) {
    return 'client-detail';
  }

  if (pathname === '/home/clients') {
    return 'clients';
  }

  if (pathname.startsWith('/home/planning')) {
    return 'planner';
  }

  if (isMeetingHubSkeletonRoute(pathname)) {
    return 'meeting-hub';
  }

  if (pathname.startsWith('/home/subscription')) {
    return 'subscription';
  }

  if (pathname.startsWith('/home/filehub')) {
    return 'filehub';
  }

  if (pathname.startsWith('/home/members')) {
    return 'members';
  }

  if (pathname.startsWith('/home/company-settings') || pathname.startsWith('/home/profile')) {
    return 'settings';
  }

  if (pathname.startsWith('/home/transaction-history')) {
    return 'transaction-history';
  }

  if (pathname.startsWith('/home/risk-assessment')) {
    return 'table';
  }

  return 'generic';
};

const SkeletonBlock: FC<{ className?: string }> = ({ className }) => (
  <span className={['wv-route-skeleton__block', className].filter(Boolean).join(' ')} />
);

export const AdvisorRouteError: FC<AdvisorRouteErrorProps> = ({
  title,
  description,
  actionLabel = 'Try again',
  onAction,
}) => {
  const handleAction =
    onAction ??
    (() => {
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    });

  return (
    <main className="wv-route-error" role="alert" aria-live="assertive">
      <div className="wv-route-error__mark" aria-hidden>
        W
      </div>
      <h1>{title}</h1>
      <p>{description}</p>
      <Button type="primary" onClick={handleAction}>
        {actionLabel}
      </Button>
    </main>
  );
};

const StatPanelSkeleton: FC<{ wide?: boolean }> = ({ wide = false }) => (
  <div className="wv-route-skeleton__panel wv-route-skeleton__panel--stat">
    <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--sm" />
    <SkeletonBlock className="wv-route-skeleton__number" />
    {wide ? (
      <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--xs" />
    ) : null}
  </div>
);

const DashboardSkeleton: FC = () => (
  <div className="wv-route-skeleton__dashboard-layout">
    <div className="wv-route-skeleton__dashboard-row">
      {repeat(2).map((index) => (
        <StatPanelSkeleton key={index} />
      ))}
    </div>

    <div className="wv-route-skeleton__dashboard-row">
      <div className="wv-route-skeleton__panel wv-route-skeleton__panel--quick-access">
        <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--card-title" />
        <div className="wv-route-skeleton__quick-access-list">
          {repeat(5).map((index) => (
            <div className="wv-route-skeleton__quick-access-item" key={index}>
              <SkeletonBlock className="wv-route-skeleton__icon" />
              <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--md" />
              <SkeletonBlock className="wv-route-skeleton__chevron" />
            </div>
          ))}
        </div>
      </div>

      <div className="wv-route-skeleton__panel wv-route-skeleton__panel--meeting">
        <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--card-title" />
        <SkeletonBlock className="wv-route-skeleton__number wv-route-skeleton__number--meeting" />
        <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--sm" />
      </div>
    </div>
  </div>
);

const ClientsSkeleton: FC = () => (
  <div className="wv-route-skeleton__clients-layout">
    <div className="wv-route-skeleton__client-stats">
      {repeat(3).map((index) => (
        <StatPanelSkeleton key={index} wide />
      ))}
    </div>

    <div className="wv-route-skeleton__panel wv-route-skeleton__panel--clients-card">
      <div className="wv-route-skeleton__clients-card-head">
        <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--card-title" />
        <div className="wv-route-skeleton__clients-actions">
          <SkeletonBlock className="wv-route-skeleton__search" />
          <SkeletonBlock className="wv-route-skeleton__button wv-route-skeleton__button--wide" />
        </div>
      </div>

      <div className="wv-route-skeleton__table">
        <div className="wv-route-skeleton__table-head">
          {repeat(5).map((index) => (
            <SkeletonBlock
              className={`wv-route-skeleton__line wv-route-skeleton__table-heading wv-route-skeleton__table-heading--${index}`}
              key={index}
            />
          ))}
        </div>
        {repeat(7).map((index) => (
          <div className="wv-route-skeleton__table-row" key={index}>
            <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--name" />
            <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--email" />
            <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--phone" />
            <SkeletonBlock className="wv-route-skeleton__pill" />
            <div className="wv-route-skeleton__row-actions">
              <SkeletonBlock className="wv-route-skeleton__action-dot" />
              <SkeletonBlock className="wv-route-skeleton__action-dot" />
            </div>
          </div>
        ))}
      </div>

      <div className="wv-route-skeleton__pagination">
        <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--pagination" />
        <div className="wv-route-skeleton__pagination-controls">
          {repeat(5).map((index) => (
            <SkeletonBlock className="wv-route-skeleton__page-button" key={index} />
          ))}
        </div>
      </div>
    </div>
  </div>
);

const ClientDetailSkeleton: FC = () => (
  <div className="wv-route-skeleton__client-detail">
    <div className="wv-route-skeleton__detail-header">
      <SkeletonBlock className="wv-route-skeleton__back-button" />
      <div className="wv-route-skeleton__detail-title-stack">
        <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--detail-title" />
        <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--md" />
      </div>
      <SkeletonBlock className="wv-route-skeleton__button wv-route-skeleton__button--wide" />
    </div>

    <div className="wv-route-skeleton__tabs">
      {repeat(4).map((index) => (
        <SkeletonBlock className="wv-route-skeleton__tab" key={index} />
      ))}
    </div>

    <div className="wv-route-skeleton__detail-layout">
      <div className="wv-route-skeleton__panel wv-route-skeleton__panel--detail-main">
        {repeat(4).map((index) => (
          <div className="wv-route-skeleton__form-row" key={index}>
            <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--sm" />
            <SkeletonBlock className="wv-route-skeleton__input" />
          </div>
        ))}
      </div>
      <div className="wv-route-skeleton__panel wv-route-skeleton__panel--detail-side">
        <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--card-title" />
        {repeat(4).map((index) => (
          <SkeletonBlock className="wv-route-skeleton__row" key={index} />
        ))}
      </div>
    </div>
  </div>
);

const ClientProfileSkeleton: FC = () => (
  <div className="wv-route-skeleton__client-profile">
    <div className="wv-route-skeleton__profile-header">
      <SkeletonBlock className="wv-route-skeleton__back-button" />
      <div className="wv-route-skeleton__detail-title-stack">
        <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--detail-title" />
        <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--lg" />
      </div>
      <SkeletonBlock className="wv-route-skeleton__button wv-route-skeleton__button--wide" />
    </div>

    <div className="wv-route-skeleton__profile-layout">
      <div className="wv-route-skeleton__profile-nav" aria-hidden>
        {repeat(7).map((index) => (
          <SkeletonBlock className="wv-route-skeleton__profile-nav-item" key={index} />
        ))}
      </div>

      <div className="wv-route-skeleton__profile-content">
        {repeat(4).map((sectionIndex) => (
          <div
            className="wv-route-skeleton__panel wv-route-skeleton__panel--profile-section"
            key={sectionIndex}
          >
            <div className="wv-route-skeleton__profile-section-head">
              <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--card-title" />
              <SkeletonBlock className="wv-route-skeleton__button" />
            </div>
            {repeat(2).map((rowIndex) => (
              <div className="wv-route-skeleton__profile-form-row" key={rowIndex}>
                <div className="wv-route-skeleton__detail-title-stack">
                  <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--sm" />
                  <SkeletonBlock className="wv-route-skeleton__input" />
                </div>
                <div className="wv-route-skeleton__detail-title-stack">
                  <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--sm" />
                  <SkeletonBlock className="wv-route-skeleton__input" />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const WealthDetailSkeleton: FC = () => (
  <div className="wv-route-skeleton__client-detail">
    <div className="wv-route-skeleton__detail-header">
      <SkeletonBlock className="wv-route-skeleton__back-button" />
      <div className="wv-route-skeleton__detail-title-stack">
        <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--detail-title" />
        <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--md" />
      </div>
      <SkeletonBlock className="wv-route-skeleton__button wv-route-skeleton__button--wide" />
    </div>

    <div className="wv-route-skeleton__tabs">
      {repeat(5).map((index) => (
        <SkeletonBlock className="wv-route-skeleton__tab" key={index} />
      ))}
    </div>

    <WealthScreenSkeleton showHeader={false} decorative />
  </div>
);

const MeetingHubSkeleton: FC = () => (
  <div className="wv-route-skeleton__meeting-hub-layout">
    <div className="wv-route-skeleton__meeting-hub-panel wv-route-skeleton__meeting-hub-panel--list">
      <div className="wv-route-skeleton__meeting-hub-search-row">
        <SkeletonBlock className="wv-route-skeleton__search wv-route-skeleton__search--meeting-hub" />
        <SkeletonBlock className="wv-route-skeleton__button wv-route-skeleton__button--meeting-hub-add" />
      </div>

      <div className="wv-route-skeleton__tabs wv-route-skeleton__tabs--meeting-hub">
        {repeat(3).map((index) => (
          <SkeletonBlock
            className="wv-route-skeleton__tab wv-route-skeleton__tab--meeting-hub"
            key={index}
          />
        ))}
      </div>

      <div className="wv-route-skeleton__meeting-hub-list">
        {repeat(2).map((groupIndex) => (
          <div className="wv-route-skeleton__meeting-hub-group" key={groupIndex}>
            <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--meeting-hub-date" />
            {repeat(3).map((rowIndex) => (
              <div className="wv-route-skeleton__meeting-hub-row" key={rowIndex}>
                <div className="wv-route-skeleton__meeting-hub-row-copy">
                  <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--meeting-hub-time" />
                  <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--meeting-hub-name" />
                </div>
                <SkeletonBlock className="wv-route-skeleton__switch" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>

    <div className="wv-route-skeleton__meeting-hub-panel wv-route-skeleton__meeting-hub-panel--detail">
      <div className="wv-route-skeleton__meeting-hub-detail-head">
        <div className="wv-route-skeleton__detail-title-stack">
          <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--detail-title" />
          <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--sm" />
        </div>
        <SkeletonBlock className="wv-route-skeleton__button wv-route-skeleton__button--wide" />
      </div>

      <div className="wv-route-skeleton__meeting-hub-info">
        {repeat(4).map((index) => (
          <div className="wv-route-skeleton__meeting-hub-info-row" key={index}>
            <SkeletonBlock className="wv-route-skeleton__icon" />
            <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--md" />
          </div>
        ))}
      </div>

      <div className="wv-route-skeleton__meeting-hub-collapse-list">
        {repeat(2).map((index) => (
          <div className="wv-route-skeleton__meeting-hub-collapse" key={index}>
            <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--card-title" />
            <SkeletonBlock className="wv-route-skeleton__chevron" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const PlannerSkeleton: FC = () => (
  <div className="wv-route-skeleton__planner-shell">
    <div className="wv-route-skeleton__planner-sidebar">
      {repeat(3).map((index) => (
        <SkeletonBlock className="wv-route-skeleton__sidebar-icon" key={index} />
      ))}
    </div>

    <div className="wv-route-skeleton__planner-content">
      <div className="wv-route-skeleton__planner-centered-panel">
        <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__planner-heading" />

        <div className="wv-route-skeleton__composer">
          <SkeletonBlock className="wv-route-skeleton__composer-textarea" />
          <div className="wv-route-skeleton__composer-footer">
            <div className="wv-route-skeleton__composer-tools">
              {repeat(3).map((index) => (
                <SkeletonBlock className="wv-route-skeleton__tool-button" key={index} />
              ))}
            </div>
            <SkeletonBlock className="wv-route-skeleton__send-button" />
          </div>
        </div>

        <div className="wv-route-skeleton__planner-mode-row">
          <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--sm" />
          <div className="wv-route-skeleton__segmented-control">
            <SkeletonBlock className="wv-route-skeleton__segment" />
            <SkeletonBlock className="wv-route-skeleton__segment" />
          </div>
        </div>

        <div className="wv-route-skeleton__category-selector">
          <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--sm" />
          <div className="wv-route-skeleton__category-tags">
            {repeat(6).map((index) => (
              <SkeletonBlock className="wv-route-skeleton__category-tag" key={index} />
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const SubscriptionSkeleton: FC = () => (
  <div className="wv-route-skeleton__subscription">
    <div className="wv-route-skeleton__subscription-copy">
      <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--detail-title" />
      <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--lg" />
    </div>
    <div className="wv-route-skeleton__pricing-grid">
      {repeat(3).map((index) => (
        <div className="wv-route-skeleton__panel wv-route-skeleton__panel--pricing" key={index}>
          <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--card-title" />
          <SkeletonBlock className="wv-route-skeleton__number" />
          {repeat(4).map((rowIndex) => (
            <SkeletonBlock className="wv-route-skeleton__row" key={rowIndex} />
          ))}
          <SkeletonBlock className="wv-route-skeleton__button" />
        </div>
      ))}
    </div>
  </div>
);

const FileHubSkeleton: FC = () => (
  <div className="wv-route-skeleton__filehub">
    <div className="wv-route-skeleton__filehub-search-row">
      <SkeletonBlock className="wv-route-skeleton__search wv-route-skeleton__search--wide" />
    </div>
    <div className="wv-route-skeleton__panel wv-route-skeleton__panel--filehub">
      <div className="wv-route-skeleton__filehub-head">
        <div className="wv-route-skeleton__tabs wv-route-skeleton__tabs--compact">
          {repeat(2).map((index) => (
            <SkeletonBlock className="wv-route-skeleton__tab" key={index} />
          ))}
        </div>
        <SkeletonBlock className="wv-route-skeleton__button wv-route-skeleton__button--wide" />
      </div>
      <TableSkeleton rows={7} />
    </div>
  </div>
);

const MembersSkeleton: FC = () => (
  <div className="wv-route-skeleton__panel wv-route-skeleton__panel--members">
    <TableSkeleton columns={3} rows={8} />
    <div className="wv-route-skeleton__pagination">
      <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--pagination" />
      <div className="wv-route-skeleton__pagination-controls">
        {repeat(5).map((index) => (
          <SkeletonBlock className="wv-route-skeleton__page-button" key={index} />
        ))}
      </div>
    </div>
  </div>
);

const SettingsSkeleton: FC = () => (
  <div className="wv-route-skeleton__settings">
    <div className="wv-route-skeleton__settings-nav">
      {repeat(4).map((index) => (
        <SkeletonBlock className="wv-route-skeleton__settings-nav-item" key={index} />
      ))}
    </div>
    <div className="wv-route-skeleton__settings-content">
      {repeat(3).map((sectionIndex) => (
        <div
          className="wv-route-skeleton__panel wv-route-skeleton__panel--settings"
          key={sectionIndex}
        >
          <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--card-title" />
          {repeat(3).map((rowIndex) => (
            <div className="wv-route-skeleton__settings-row" key={rowIndex}>
              <div className="wv-route-skeleton__detail-title-stack">
                <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--sm" />
                <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--xs" />
              </div>
              <SkeletonBlock className="wv-route-skeleton__input" />
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const TransactionHistorySkeleton: FC = () => (
  <div className="wv-route-skeleton__transaction">
    <div className="wv-route-skeleton__toolbar">
      <div className="wv-route-skeleton__toolbar-actions">
        <SkeletonBlock className="wv-route-skeleton__button" />
        <SkeletonBlock className="wv-route-skeleton__button" />
      </div>
    </div>
    <div className="wv-route-skeleton__panel wv-route-skeleton__panel--transaction-stat">
      <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--sm" />
      <SkeletonBlock className="wv-route-skeleton__number" />
    </div>
    <div className="wv-route-skeleton__panel wv-route-skeleton__panel--transaction-table">
      <TableSkeleton columns={4} rows={8} />
    </div>
  </div>
);

const TablePageSkeleton: FC = () => (
  <div className="wv-route-skeleton__table-page">
    <div className="wv-route-skeleton__toolbar">
      <SkeletonBlock className="wv-route-skeleton__search" />
      <SkeletonBlock className="wv-route-skeleton__button" />
    </div>
    <div className="wv-route-skeleton__panel wv-route-skeleton__panel--table-page">
      <TableSkeleton rows={8} />
    </div>
  </div>
);

const GenericSkeleton: FC = () => (
  <>
    <div className="wv-route-skeleton__toolbar">
      <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--lg" />
      <SkeletonBlock className="wv-route-skeleton__button" />
    </div>
    <div className="wv-route-skeleton__grid">
      {repeat(4).map((index) => (
        <div className="wv-route-skeleton__panel" key={index}>
          <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--md" />
          <SkeletonBlock className="wv-route-skeleton__line wv-route-skeleton__line--sm" />
          <SkeletonBlock className="wv-route-skeleton__row" />
        </div>
      ))}
    </div>
  </>
);

function TableSkeleton({ columns = 5, rows = 7 }: { columns?: number; rows?: number }) {
  const tableGridTemplate = `repeat(${columns}, minmax(120px, 1fr))`;

  return (
    <div className="wv-route-skeleton__table">
      <div
        className="wv-route-skeleton__table-head"
        style={{ gridTemplateColumns: tableGridTemplate }}
      >
        {repeat(columns).map((index) => (
          <SkeletonBlock
            className={`wv-route-skeleton__line wv-route-skeleton__table-heading wv-route-skeleton__table-heading--${index}`}
            key={index}
          />
        ))}
      </div>
      {repeat(rows).map((index) => (
        <div
          className="wv-route-skeleton__table-row"
          style={{ gridTemplateColumns: tableGridTemplate }}
          key={index}
        >
          {repeat(columns).map((columnIndex) => (
            <SkeletonBlock
              className={`wv-route-skeleton__line wv-route-skeleton__line--table-cell-${columnIndex}`}
              key={columnIndex}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

const getSkeletonContent = (variant: RouteSkeletonVariant) => {
  switch (variant) {
    case 'dashboard':
      return <DashboardSkeleton />;
    case 'clients':
      return <ClientsSkeleton />;
    case 'wealth-detail':
      return <WealthDetailSkeleton />;
    case 'client-profile':
      return <ClientProfileSkeleton />;
    case 'client-detail':
      return <ClientDetailSkeleton />;
    case 'meeting-hub':
      return <MeetingHubSkeleton />;
    case 'planner':
      return <PlannerSkeleton />;
    case 'subscription':
      return <SubscriptionSkeleton />;
    case 'filehub':
      return <FileHubSkeleton />;
    case 'members':
      return <MembersSkeleton />;
    case 'settings':
      return <SettingsSkeleton />;
    case 'transaction-history':
      return <TransactionHistorySkeleton />;
    case 'table':
      return <TablePageSkeleton />;
    default:
      return <GenericSkeleton />;
  }
};

export const AdvisorRouteSkeleton: FC<{ pathname?: string; reserveAppShell?: boolean }> = ({
  pathname = '/home',
  reserveAppShell = false,
}) => {
  const variant = getRouteSkeletonVariant(pathname);

  return (
    <section
      className={`wv-route-skeleton wv-route-skeleton--${variant}${
        reserveAppShell ? ' wv-route-skeleton--app-shell-reserved' : ''
      }`}
      role="status"
      aria-busy="true"
      aria-label="Loading page content"
      aria-live="polite"
    >
      {getSkeletonContent(variant)}
    </section>
  );
};
